import { AuthContext } from "../../../contexts/AuthContext";
import { useContext, useState, useEffect, useMemo } from "react";
import "./Hotels.scss";
import PageTitle from "../../../components/PageTitle/PageTitle";
import { resolveUrl } from "../../../utils/resolveUrl";
import slugify from "../../../utils/slugify";
import { CiMapPin } from "react-icons/ci";
import { CiEdit } from "react-icons/ci";
import { ReactComponent as Close } from "../../../assets/x.svg";
import AlertModal from "../../../components/AlertModal/AlertModal";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import {
  DynamicFileInput,
  DynamicImagePreview,
  DynamicInput,
  DynamicSelect,
} from "../../../components/Inputs/Inputs";
import Button from "../../../components/Button/Button";
import { Modal } from "../../../components/Modal/Modal";
import ImagePlaceholder from "../../../assets/image_placeholder.png";

const emptyCreate = {
  hotel_slug: "",
  name: "",
  img_url: "",
  location: "",
  street: "",
  number: "",
  postal_code: "",
  city: "",
  country: "",
  file: null,
  previewUrl: "",
};

export default function Hotels() {
  const {
    axiosAuth,
    accessibleGroups = [],
    accessibleHotels = [],
    currentUser,
  } = useContext(AuthContext);
  const isHotelAdmin = currentUser?.role === "hotel_admin";
  const groups = accessibleGroups;
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [hotelsList, setHotelsList] = useState([]);
  const [autoSlug, setAutoSlug] = useState(true);

  // criar / editar
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreate);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editForm, setEditForm] = useState({
    id: null,
    hotel_slug: "",
    name: "",
    img_url: "",
    location: "",
    street: "",
    number: "",
    postal_code: "",
    city: "",
    country: "",
    whatsapp_fallback_number: "",
    // upload
    file: null,
    previewUrl: "",
  });

  // ui
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorModal, setErrorModal] = useState({
    open: false,
    title: "",
    message: "",
  });
  const [confirmationModal, setConfirmationModal] = useState({
    open: false,
    hotelId: null,
  });

  // carregar hotéis quando escolhe grupo
  useEffect(() => {
    if (!selectedGroupId) {
      setHotelsList([]);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const { data } = await axiosAuth.get(
          `/hotels/group/${selectedGroupId}`
        );
        // se for hotel_admin, filtra apenas os seus hotéis
        let list = data || [];
        if (isHotelAdmin && accessibleHotels?.length) {
          const allowed = new Set(accessibleHotels.map((h) => h.id));
          list = list.filter((h) => allowed.has(h.id));
        }
        setHotelsList(list);
      } catch (err) {
        openError(
          "Erro ao carregar hotéis do grupo",
          err?.response?.data?.detail || err.message
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedGroupId, axiosAuth, isHotelAdmin, accessibleHotels]);

  // se existir só um grupo acessível, seleciona por defeito
  useEffect(() => {
    if (!selectedGroupId && groups?.length > 1) {
      setSelectedGroupId(String(groups[0].id));
    }
  }, [groups, selectedGroupId]);

  function openError(title, message) {
    setErrorModal({ open: true, title, message });
  }

  // criar novo hotel
  function openCreateModal() {
    if (createForm.previewUrl) {
      URL.revokeObjectURL(createForm.previewUrl);
    }
    setCreateForm({ ...emptyCreate });
    setAutoSlug(true);
    setIsCreateModalVisible(true);
  }

  function updateCreate(name, value) {
    setCreateForm((p) => {
      let next = { ...p, [name]: value };
      if (name === "name" && autoSlug) {
        next.hotel_slug = slugify(value);
      }
      return next;
    });
  }

  function handleCreateImage(e) {
    const file = e.target.files?.[0] ?? null;
    // limpa URL anterior
    if (createForm.previewUrl) {
      URL.revokeObjectURL(createForm.previewUrl);
    }
    setCreateForm((p) => ({
      ...p,
      file,
      previewUrl: file ? URL.createObjectURL(file) : "",
    }));
  }

  async function handleSubmitCreate() {
    if (!selectedGroupId) {
      openError("Validação", "Selecione primeiro um grupo.");
      return;
    }
    if (!createForm.name.trim()) {
      openError("Validação", "O nome do hotel é obrigatório.");
      return;
    }
    if (!createForm.hotel_slug.trim()) {
      openError("Validação", "O slug do hotel é obrigatório.");
      return;
    }
    setSaving(true);
    try {
      // multipart/form-data para o backend aceitar
      const fd = new FormData();
      fd.append("group_id", String(selectedGroupId));
      fd.append("hotel_slug", createForm.hotel_slug);
      fd.append("name", createForm.name);
      fd.append("location", createForm.location ?? "");
      fd.append("street", createForm.street ?? "");
      fd.append("number", createForm.number ?? "");
      fd.append("postal_code", createForm.postal_code ?? "");
      fd.append("city", createForm.city ?? "");
      fd.append("country", createForm.country ?? "");
      if (createForm.file) fd.append("image", createForm.file);
      await axiosAuth.post("/hotels/", fd);

      // refresh lista
      const { data } = await axiosAuth.get(`/hotels/group/${selectedGroupId}`);
      let list = data || [];
      if (isHotelAdmin && accessibleHotels?.length) {
        const allowed = new Set(accessibleHotels.map((h) => h.id));
        list = list.filter((h) => allowed.has(h.id));
      }
      setHotelsList(list);
      setIsCreateModalVisible(false);
    } catch (err) {
      openError(
        "Erro ao criar hotel",
        err?.response?.data?.detail || err.message
      );
    } finally {
      setSaving(false);
      // cleanup do ObjectURL
      if (createForm.previewUrl) {
        URL.revokeObjectURL(createForm.previewUrl);
        setCreateForm((p) => ({ ...p, previewUrl: "" }));
      }
    }
  }

  // editar
  function openEditModal(h) {
    // limpa preview anterior
    if (editForm.previewUrl) {
      URL.revokeObjectURL(editForm.previewUrl);
    }
    setEditForm({
      id: h.id,
      hotel_slug: h.hotel_slug || "",
      name: h.name || "",
      img_url: h.img_url || "",
      location: h.location || "",
      street: h.street || "",
      number: h.number || "",
      postal_code: h.postal_code || "",
      city: h.city || "",
      country: h.country || "",
      whatsapp_fallback_number: h.whatsapp_fallback_number || "",
      file: null,
      previewUrl: "",
    });
    setIsEditModalVisible(true);
  }

  function updateEdit(name, value) {
    setEditForm((p) => ({ ...p, [name]: value }));
  }

  function handleEditImage(e) {
    const file = e.target.files?.[0] ?? null;
    if (editForm.previewUrl) {
      URL.revokeObjectURL(editForm.previewUrl);
    }
    setEditForm((p) => ({
      ...p,
      file,
      previewUrl: file ? URL.createObjectURL(file) : "",
    }));
  }

  async function handleSubmitEdit() {
    if (!editForm.id) return;
    if (!editForm.name.trim()) {
      openError("Validação", "O nome do hotel é obrigatório.");
      return;
    }
    if (!editForm.hotel_slug.trim()) {
      openError("Validação", "O slug do hotel é obrigatório.");
      return;
    }
    setSaving(true);
    try {
      // multipart update; só envia o que interessa
      const fd = new FormData();
      fd.append("hotel_slug", editForm.hotel_slug);
      fd.append("name", editForm.name);
      if (editForm.location != null) fd.append("location", editForm.location);
      if (editForm.street != null) fd.append("street", editForm.street);
      if (editForm.number != null) fd.append("number", editForm.number);
      if (editForm.postal_code != null)
        fd.append("postal_code", editForm.postal_code);
      if (editForm.city != null) fd.append("city", editForm.city);
      if (editForm.country != null) fd.append("country", editForm.country);
      if (editForm.whatsapp_fallback_number != null)
        fd.append("whatsapp_fallback_number", editForm.whatsapp_fallback_number);
      if (editForm.file) fd.append("image", editForm.file);

      await axiosAuth.put(`/hotels/${editForm.id}`, fd);

      const { data } = await axiosAuth.get(`/hotels/group/${selectedGroupId}`);
      let list = data || [];
      if (isHotelAdmin && accessibleHotels?.length) {
        const allowed = new Set(accessibleHotels.map((h) => h.id));
        list = list.filter((h) => allowed.has(h.id));
      }
      setHotelsList(list);
      setIsEditModalVisible(false);
    } catch (err) {
      openError(
        "Erro ao guardar hotel",
        err?.response?.data?.detail || err.message
      );
    } finally {
      setSaving(false);
      if (editForm.previewUrl) {
        URL.revokeObjectURL(editForm.previewUrl);
        setEditForm((p) => ({ ...p, previewUrl: "" }));
      }
    }
  }

  // apagar
  function handleDeleteHotel(hotelId) {
    setConfirmationModal({ open: true, hotelId });
  }

  async function confirmDeleteHotel() {
    const id = confirmationModal.hotelId;
    if (!id) return;
    try {
      await axiosAuth.delete(`/hotels/${id}`);
      const { data } = await axiosAuth.get(`/hotels/group/${selectedGroupId}`);
      let list = data || [];
      if (isHotelAdmin && accessibleHotels.length) {
        const allowed = new Set(accessibleHotels.map((h) => h.id));
        list = list.filter((h) => allowed.has(h.id));
      }
      setHotelsList(list);
      setIsEditModalVisible(false);
    } catch (err) {
      openError(
        "Erro ao apagar hotel",
        err?.response?.data?.detail || err.message
      );
    } finally {
      setConfirmationModal({ open: false, hotelId: null });
    }
  }

  // render
  const groupOptions = useMemo(
    () =>
      groups.map((g) => ({
        value: String(g.id),
        label: g.name,
      })),
    [groups]
  );

  const currentEditPreview =
    editForm.previewUrl ||
    (editForm.img_url ? resolveUrl(editForm.img_url) : "");

  return (
    <div className="hotels">
      {errorModal.open && (
        <AlertModal
          variant="danger"
          title={errorModal.title || "Ocorreu um erro"}
          message={errorModal.message}
          onClose={() => setErrorModal({ open: false, message: "", title: "" })}
        />
      )}
      {confirmationModal.open && (
        <ConfirmationModal
          variant="danger"
          title="Apagar hotel?"
          message="Deseja apagar este hotel?"
          secText="Fechar"
          varText="Apagar"
          onConfirm={confirmDeleteHotel}
          onCancel={() => setConfirmationModal({ open: false, hotelId: null })}
        />
      )}

      <PageTitle link="" title="Hotéis" />

      <div className="filters">
        <div className="left">
          <DynamicSelect
            value={selectedGroupId}
            onChange={(e) => setSelectedGroupId(e.target.value)}
            options={groupOptions}
            placeholder="Escolher um grupo"
            label="Cliente"
            disabled={loading}
          />
        </div>
        <div className="right">
          {selectedGroupId && (
            <Button type="button" onClick={openCreateModal} disabled={loading}>
              Novo hotel
            </Button>
          )}
        </div>
      </div>

      <div className="container">
        {hotelsList.map((h) => {
          const addressParts = [
            [h.street, h.number].filter(Boolean).join(" "),
            [h.postal_code, h.city].filter(Boolean).join(" "),
            h.country,
          ].filter(Boolean);
          return (
            <div key={h.id} className="hotel-card">
              <div className="image">
                {h.img_url ? (
                  <img src={resolveUrl(h.img_url)} alt={h.name} />
                ) : (
                  <img src={ImagePlaceholder} alt="Image Placeholder" />
                )}
              </div>
              <div className="content">
                <div className="content-header">
                  <h3>{h.name}</h3>
                  {h.hotel_slug && <span className="slug">{h.hotel_slug}</span>}
                </div>
                <div className="location">
                  <CiMapPin size={14} />
                  <p>{h.location || "-"}</p>
                </div>
                {addressParts.length > 0 && (
                  <p className="address">{addressParts.join(" · ")}</p>
                )}
                <div className="edit">
                  <div className="button" onClick={() => openEditModal(h)}>
                    <button>Editar</button>
                    <span>
                      <CiEdit size={14} />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {!loading && selectedGroupId && hotelsList.length === 0 && (
        <div className="empty">Sem hotéis neste grupo.</div>
      )}
      {isCreateModalVisible && (
        <Modal
          isVisible={isCreateModalVisible}
          onClose={() => setIsCreateModalVisible(false)}
          title="Novo hotel"
          saveText="Adicionar"
          onSave={handleSubmitCreate}
          disableActions={saving}
        >
          <div className="row">
            <DynamicImagePreview imageSrc={createForm.previewUrl} />
          </div>
          <div className="row">
            <DynamicFileInput
              name="newImage"
              label="Imagem:"
              onChange={handleCreateImage}
            />
          </div>
          <div className="row">
            <DynamicInput
              required
              name="name"
              label="Nome do hotel"
              value={createForm.name}
              onChange={(n, v) => updateCreate(n, v)}
            />
          </div>
          <div className="row">
            <DynamicInput
              required
              name="hotel_slug"
              label="Slug"
              value={createForm.hotel_slug}
              onChange={(n, v) => {
                setAutoSlug(false);
                updateCreate(n, slugify(v));
              }}
            />
          </div>
          <div className="row two-col">
            <DynamicInput
              name="location"
              label="Localização"
              required
              value={createForm.location}
              onChange={(n, v) => updateCreate(n, v)}
            />
            <DynamicInput
              name="city"
              label="Cidade"
              value={createForm.city}
              onChange={(n, v) => updateCreate(n, v)}
            />
          </div>
          <div className="row two-col">
            <DynamicInput
              name="country"
              label="País"
              value={createForm.country}
              onChange={(n, v) => updateCreate(n, v)}
            />
            <DynamicInput
              name="postal_code"
              label="Código Postal"
              value={createForm.postal_code}
              onChange={(n, v) => updateCreate(n, v)}
            />
          </div>
          <div className="row two-col">
            <DynamicInput
              name="street"
              label="Rua"
              value={createForm.street}
              onChange={(n, v) => updateCreate(n, v)}
            />
            <DynamicInput
              name="number"
              label="Número"
              value={createForm.number}
              onChange={(n, v) => updateCreate(n, v)}
            />
          </div>
        </Modal>
      )}
      {isEditModalVisible && (
        <Modal
          isVisible={isEditModalVisible}
          onClose={() => setIsEditModalVisible(false)}
          title="Editar hotel"
          saveText="Guardar"
          onSave={handleSubmitEdit}
          deleteText="Apagar"
          onDelete={() => handleDeleteHotel(editForm.id)}
          disableActions={saving}
        >
          <div className="row">
            <DynamicImagePreview imageSrc={currentEditPreview} />
          </div>
          <div className="row">
            <DynamicFileInput
              label="Nova imagem:"
              onChange={handleEditImage}
              name="newImage"
            />
          </div>
          <div className="row">
            <DynamicInput
              required
              name="name"
              label="Nome do hotel"
              value={editForm.name}
              onChange={(n, v) => updateEdit(n, v)}
            />
          </div>
          <div className="row">
            <DynamicInput
              required
              name="hotel_slug"
              label="Slug"
              value={editForm.hotel_slug}
              onChange={(n, v) => updateEdit(n, slugify(v))}
            />
          </div>
          {/* <div className="row">
            <DynamicInput
              name="img_url"
              label="URL da imagem"
              value={editForm.img_url}
              onChange={(n, v) => updateEdit(n, v)}
            />
          </div> */}
          <div className="row two-col">
            <DynamicInput
              name="location"
              label="Localização"
              value={editForm.location}
              onChange={(n, v) => updateEdit(n, v)}
            />
            <DynamicInput
              name="city"
              label="Cidade"
              value={editForm.city}
              onChange={(n, v) => updateEdit(n, v)}
            />
          </div>
          <div className="row two-col">
            <DynamicInput
              name="country"
              label="País"
              value={editForm.country}
              onChange={(n, v) => updateEdit(n, v)}
            />
            <DynamicInput
              name="postal_code"
              label="Código Postal"
              value={editForm.postal_code}
              onChange={(n, v) => updateEdit(n, v)}
            />
          </div>
          <div className="row two-col">
            <DynamicInput
              name="street"
              label="Rua"
              value={editForm.street}
              onChange={(n, v) => updateEdit(n, v)}
            />
            <DynamicInput
              name="number"
              label="Número"
              value={editForm.number}
              onChange={(n, v) => updateEdit(n, v)}
            />
          </div>
          <div className="row">
            <DynamicInput
              name="whatsapp_fallback_number"
              label="WhatsApp de recurso (Atendimento Humano)"
              placeholder="Ex.: +351912345678"
              value={editForm.whatsapp_fallback_number}
              onChange={(n, v) => updateEdit(n, v)}
            />
          </div>
          <p className="field-hint">
            Enquanto o Atendimento Humano não estiver licenciado/ligado para este hotel,
            "Falar com o Staff" no chatbot abre o WhatsApp deste número em vez de enviar
            um pedido que nunca chegaria a lado nenhum. Deixa em branco para desativar.
          </p>
        </Modal>
      )}
    </div>
  );
}
